import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '../api';
import { useUser } from '../providers/UserProvider';
import { listBetaSearches, saveBetaSearch, setSearchIncluded, type BetaSearch } from '../repositories/betaSearchRepository';
import { rememberReaderAction } from '../utils/readerPendingAction';
import { readerEvent } from '../utils/readerTelemetry';
import { matchingSavedFeed } from '../utils/savedFeedMatch';

/**
 * Saving a search as a feed, as the snapshot reader's search did: Save feed,
 * or Update feed when the reader already has a feed with this prompt (it then
 * only sets that feed's Show in Home); the Show in Home switch records intent
 * until the button applies it, then changes the saved feed at once. Signed
 * out, saving goes through sign-up and is replayed after it
 * (`readerPendingAction`), which this picks up on return.
 */
export function useSearchFeedSave(query: string) {
  const { isLoggedIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [feeds, setFeeds] = useState<BetaSearch[]>([]);
  const [included, setIncluded] = useState(true);
  const [saved, setSaved] = useState<string | null>(null);
  const [updated, setUpdated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const matchingFeed = matchingSavedFeed(feeds, query);
  // The query on screen now: a save that finishes after the reader moved to another search must not mark that one saved.
  const currentQuery = useRef(query);
  currentQuery.current = query;

  // Only an account has saved feeds to match: signed out, the catalog lists the public feeds instead.
  useEffect(() => {
    let disposed = false;
    if (!isLoggedIn) { setFeeds([]); return; }
    listBetaSearches().then(catalog => { if (!disposed) setFeeds(catalog.searches); })
      .catch(err => { if (!disposed) setError(extractErrorMessage(err)); });
    return () => { disposed = true; };
  }, [isLoggedIn]);
  useEffect(() => { setIncluded(matchingFeed?.included ?? true); }, [matchingFeed?.id, matchingFeed?.included]);
  // A new search starts from its own Show in Home: its saved feed's, else on, never the last search's choice.
  useEffect(() => {
    setSaved(null); setUpdated(false); setMessage(''); setError('');
    setIncluded(matchingSavedFeed(feeds, query)?.included ?? true);
  }, [query]);
  useEffect(() => {
    const resumed = sessionStorage.getItem('reader_resumed_save');
    if (resumed) { setSaved(resumed); sessionStorage.removeItem('reader_resumed_save'); }
  }, [isLoggedIn]);

  const save = async () => {
    if (saving || !query.trim()) return;
    if (!isLoggedIn) {
      readerEvent('login_gate_hit', { trigger_action: 'feed_add', trigger: 'save_feed' });
      rememberReaderAction({ kind: 'save_feed', description: query.trim(), showInHome: included });
      navigate('/signup', { state: { backgroundLocation: location, from: location } });
      return;
    }
    setSaving(true); setError('');
    const savedFor = query;
    try {
      const feed = matchingFeed ?? await saveBetaSearch(query.trim(), undefined, included);
      if (matchingFeed) await setSearchIncluded(feed.id, included);
      setFeeds(items => matchingFeed ? items.map(item => item.id === feed.id ? { ...item, included } : item) : [...items, feed]);
      readerEvent('feed_add', { search_id: feed.id, method: matchingFeed ? 'update_button' : 'save_new_button',
        action: matchingFeed ? 'include' : 'create', show_in_home: included, feeds_v2: true });
      if (currentQuery.current !== savedFor) return;
      setSaved(feed.id); setUpdated(Boolean(matchingFeed));
      setMessage(`${matchingFeed ? 'Updated' : 'Saved'} “${feed.name}” · ${included ? 'shown in Home' : 'not in Home'}`);
    } catch (err) { if (currentQuery.current === savedFor) setError(`Could not save this feed: ${extractErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const changeIncluded = async (on: boolean) => {
    if (!saved || !isLoggedIn) { setIncluded(on); return; }
    setSaving(true); setError('');
    const toggledFor = query;
    try {
      await setSearchIncluded(saved, on);
      // The cached feed too: a later search of this text matches it and must show the switch as it now is.
      setFeeds(items => items.map(item => item.id === saved ? { ...item, included: on } : item));
      readerEvent('feed_home_toggle', { feed_id: saved, on, method: 'results_switch', feeds_v2: true });
      // As with a save: once the reader is on another search, this one's switch and notice are not shown there.
      if (currentQuery.current !== toggledFor) return;
      setIncluded(on);
      setMessage(on ? 'Shown in Home' : 'Removed from Home. Still saved in Your feeds.');
    } catch (err) { if (currentQuery.current === toggledFor) setError(extractErrorMessage(err)); }
    finally { setSaving(false); }
  };

  // One already in Home with the switch still on is simply saved.
  const alreadySaved = !saved && Boolean(matchingFeed?.included) && included;
  const label = saving ? 'Saving…' : saved ? (updated ? 'Updated' : 'Saved') : alreadySaved ? 'Saved' : matchingFeed ? 'Update feed' : 'Save feed';
  return {
    matchingFeed, included, saved, saving, message, error, label,
    done: Boolean(saved) || alreadySaved,
    save, changeIncluded, dismissMessage: () => setMessage(''), dismissError: () => setError(''),
  };
}
